import React, {useState, useEffect} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
    Box,
    Alert,
} from '@mui/material';
import {sendJsonToApi} from '../../../../API/apiHelper';
import {toast} from 'react-toastify';

function ApproveRowDialog({
                              open,
                              handleClose,
                              editRow,
                              onApproveSuccess,
                          }) {

    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [checking, setChecking] = useState(false);

    const formatDeduction = (deduction) => {
        if (deduction === null || deduction === undefined) return '0';
        return typeof deduction === 'number' ? deduction.toFixed(2) : deduction;
    };

    // Check validation when dialog opens
    useEffect(() => {
        if (open && editRow.id) {
            checkValidation();
        } else {
            setValidationError(null);
        }
    }, [open, editRow.id]);

    const checkValidation = async () => {
        try {
            setChecking(true);
            setValidationError(null);
            const userId = localStorage.getItem('user_id');

            const requestData = {
                id: editRow.id,
                user_id: userId,
                ...editRow,
            };

            const response = await sendJsonToApi('post', '/api/financialtools/deduction-control/validate-approval', requestData);

            if (response.data.success) {
                // Validation passed, allow confirmation
                setChecking(false);
            } else {
                // Validation failed, show error
                setValidationError(response.data.message);
                setChecking(false);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'An error occurred while checking validation.';
            setValidationError(errorMessage);
            setChecking(false);
        }
    };

    const sendToServer = async () => {
        try {
            setLoading(true);
            const userId = localStorage.getItem('user_id');

            if (!editRow.id) {
                setLoading(false);
                toast.error('System error: This row without ID.');
                return;
            }

            const requestData = {
                id: editRow.id,
                user_id: userId,
                ...editRow,
            };

            const response = await sendJsonToApi('post', '/api/financialtools/deduction-control/approve', requestData);

            if (response.data.success) {
                toast.success(response.data.message);
                setLoading(false);
                handleClose();
                onApproveSuccess();
            } else {
                setLoading(false);
                toast.error('Error: ' + response.data.message);
            }
        } catch (error) {
            setLoading(false);
            const errorMessage = error.response?.data?.message || error.message || 'An error occurred while approving the deduction.';
            toast.error(errorMessage);
            console.error('An error occurred while approving:', error);
        }
    };

    const deductionValue = formatDeduction(editRow.deduction);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {validationError ? 'Approval Validation Error' : 'Confirm Deduction Approval'}
            </DialogTitle>
            <DialogContent>
                {checking ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={150}>
                        <CircularProgress />
                    </Box>
                ) : validationError ? (
                    <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body1">
                            {validationError}
                        </Typography>
                    </Alert>
                ) : (
                    <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                        Are you sure you want to approve deduction <strong>${deductionValue}</strong>?
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary" disabled={loading || checking}>
                    {validationError ? 'Close' : 'Cancel'}
                </Button>
                {!validationError && !checking && (
                    <Button onClick={sendToServer} color="success" disabled={loading} variant="contained">
                        Confirm
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default ApproveRowDialog;